import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type CertificateFieldConfig } from '@/api/admin'
import { AlignLeft, AlignCenter, AlignRight, Type, Images, ImagePlus, X, PenLine, QrCode } from 'lucide-react'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import { isUs } from '@/lib/dates'

// ─── Fonts ───────────────────────────────────────────────────────────────────
const FONTS = [
  { id: 'Inter',            label: 'Inter',            css: 'Inter, sans-serif' },
  { id: 'Montserrat',       label: 'Montserrat',       css: 'Montserrat, sans-serif' },
  { id: 'Lora',             label: 'Lora',             css: 'Lora, serif' },
  { id: 'Playfair Display', label: 'Playfair Display', css: '"Playfair Display", serif' },
  { id: 'Cinzel',           label: 'Cinzel',           css: 'Cinzel, serif' },
]

// ─── Types ───────────────────────────────────────────────────────────────────
type FieldId = Exclude<CertificateFieldConfig['field'], 'static' | 'companyLogo' | 'image' | 'verificationQr' | 'trainerSignature' | 'companySignature'>
type DateFormatId = 'short' | 'medium' | 'long' | 'full' | 'month-year'

const DYNAMIC_FIELDS: FieldId[] = [
  'recipientName', 'courseName', 'courseCode',
  'completedDate', 'expiryDate', 'companyName',
]

const FORMAT_EXAMPLE_DATE = new Date(2025, 0, 15)

const DATE_FORMATS: { id: DateFormatId; label: string; example: (d: Date) => string }[] = [
  { id: 'short',      label: 'Short',        example: d => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) },
  { id: 'medium',     label: 'Medium',       example: d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) },
  { id: 'long',       label: 'Long',         example: d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) },
  { id: 'full',       label: 'Full',         example: d => d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) },
  { id: 'month-year', label: 'Month & year', example: d => d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) },
]

function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th'
  switch (day % 10) {
    case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'
  }
}

// Mirrors the backend certificate renderer: US tenants get month-first layouts, everyone else the original en-GB ones.
function formatDate(d: Date, formatId?: DateFormatId, ordinal?: boolean, region?: string | null): string {
  const day = d.getDate()
  const sfx = ordinal ? ordinalSuffix(day) : ''
  if (isUs(region)) {
    const part = (o: Intl.DateTimeFormatOptions) => d.toLocaleDateString('en-US', o)
    const dayText = `${day}${sfx}`
    switch (formatId ?? 'long') {
      case 'short':      return part({ month: '2-digit', day: '2-digit', year: 'numeric' })
      case 'medium':     return `${part({ month: 'short' })} ${dayText}, ${d.getFullYear()}`
      case 'full':       return `${part({ weekday: 'long' })}, ${part({ month: 'long' })} ${dayText}, ${d.getFullYear()}`
      case 'month-year': return part({ month: 'long', year: 'numeric' })
      default:           return `${part({ month: 'long' })} ${dayText}, ${d.getFullYear()}`
    }
  }
  const locale = 'en-GB'
  switch (formatId ?? 'long') {
    case 'short':      return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    case 'medium':     return ordinal ? `${day}${sfx} ${d.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}` : d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    case 'long':       return ordinal ? `${day}${sfx} ${d.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}` : d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
    case 'full':       return ordinal ? `${d.toLocaleDateString(locale, { weekday: 'long' })}, ${day}${sfx} ${d.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}` : d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    case 'month-year': return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
  }
}

function previewText(cfg: CertificateFieldConfig, companyName: string | null, region?: string | null): string {
  const today = new Date()
  switch (cfg.field) {
    case 'recipientName':      return 'Recipient Name'
    case 'courseName':         return 'Course Name'
    case 'courseCode':         return 'CODE-01'
    case 'completedDate':      return formatDate(today, cfg.dateFormat, cfg.dateOrdinal, region)
    case 'expiryDate':         return formatDate(new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()), cfg.dateFormat, cfg.dateOrdinal, region)
    case 'companyName':        return companyName || 'Your Company'
    case 'static':             return cfg.staticText || 'Custom text'
    case 'companyLogo':        return ''
    case 'image':              return ''
    case 'verificationQr':     return ''
    case 'trainerSignature':   return ''
    case 'companySignature':   return ''
  }
}

function fieldLabel(fieldId: FieldId | 'static' | 'companyLogo' | 'image' | 'verificationQr' | 'trainerSignature' | 'companySignature') {
  const labels: Record<string, string> = {
    recipientName: 'Recipient Name', courseName: 'Course Name', courseCode: 'Course Code',
    completedDate: 'Completion Date', expiryDate: 'Expiry Date', companyName: 'Company Name',
    static: 'Custom text', companyLogo: 'Company Logo', image: 'Image',
    verificationQr: 'Verification QR',
    trainerSignature: 'Trainer Signature', companySignature: "Director's Signature",
  }
  return labels[fieldId] ?? fieldId
}

function defaultElement(fieldId: FieldId): CertificateFieldConfig {
  const positions: Partial<Record<FieldId, { x: number; y: number; align: 'left' | 'center' | 'right' }>> = {
    companyName:   { x: 50, y: 12, align: 'center' },
    courseName:    { x: 50, y: 42, align: 'center' },
    recipientName: { x: 50, y: 55, align: 'center' },
    courseCode:    { x: 4,  y: 95, align: 'left'   },
    completedDate: { x: 25, y: 68, align: 'center' },
    expiryDate:    { x: 75, y: 68, align: 'center' },
  }
  const pos = positions[fieldId] ?? { x: 50, y: 50, align: 'center' }
  const isDate = fieldId === 'completedDate' || fieldId === 'expiryDate'
  return {
    field: fieldId,
    x: pos.x, y: pos.y,
    fontSize: fieldId === 'recipientName' ? 36 : 22,
    bold: fieldId === 'recipientName',
    align: pos.align,
    color: '#1a1a1a',
    fontFamily: 'Inter',
    ...(isDate ? { dateOrdinal: true } : {}),
  }
}

function parseFields(json: string | null): CertificateFieldConfig[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function uniqueKey(cfg: CertificateFieldConfig) {
  return (cfg.field === 'static' || cfg.field === 'image') ? (cfg.elementId ?? cfg.field) : cfg.field
}

function isSignatureField(field: string) {
  return field === 'trainerSignature' || field === 'companySignature'
}

function isImageLikeField(field: string) {
  return field === 'companyLogo' || field === 'image' || field === 'verificationQr'
    || field === 'trainerSignature' || field === 'companySignature'
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function CertificateFieldDesigner({
  templateId,
  systemTemplateId,
  primaryColour,
  backgroundColour,
  fieldsJson,
  isPortrait = false,
  onChange,
}: {
  templateId: string | null
  systemTemplateId?: string | null
  primaryColour?: string | null
  backgroundColour?: string | null
  fieldsJson: string | null
  isPortrait?: boolean
  onChange: (json: string) => void
}) {
  const { data: templates = [] } = useQuery({
    queryKey: ['certificate-templates'],
    queryFn: () => adminApi.getCertificateTemplates().then(r => r.data),
  })
  const { data: systemTemplates = [] } = useQuery({
    queryKey: ['system-certificate-templates'],
    queryFn: () => adminApi.getSystemCertificateTemplates().then(r => r.data),
  })
  const { data: companyProfile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })
  const { data: certImages = [] } = useQuery({
    queryKey: ['certificate-images'],
    queryFn: () => adminApi.getCertificateImages().then(r => r.data),
  })

  const queryClient = useQueryClient()
  const imageFileInputRef = useRef<HTMLInputElement>(null)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  const [pendingImageName, setPendingImageName] = useState('')

  const uploadImageMutation = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) =>
      adminApi.uploadCertificateImage(name, file).then(r => r.data),
    onSuccess: (uploaded) => {
      queryClient.invalidateQueries({ queryKey: ['certificate-images'] })
      setPendingImageFile(null)
      setPendingImageName('')
      // Immediately place the just-uploaded image on the canvas
      addImage(uploaded.url, uploaded.name)
    },
  })

  // Resolve the background image URL — from tenant template or system template
  const template = templateId ? templates.find(t => t.id === templateId) : null
  const systemTemplate = systemTemplateId ? systemTemplates.find(t => t.id === systemTemplateId) : null
  const backgroundImageUrl = template?.backgroundImageUrl ?? systemTemplate?.thumbnailUrl ?? null
  const companyName = companyProfile?.displayName ?? null
  const region = companyProfile?.region
  const isPro = companyProfile?.planTier === 'Pro'

  const [elements, setElements] = useState<CertificateFieldConfig[]>(() => parseFields(fieldsJson))
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [showProDialog, setShowProDialog] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [snapGuides, setSnapGuides] = useState<{ xLines: number[]; yLines: number[] }>({ xLines: [], yLines: [] })
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 566 })
  const canvasRef = useRef<HTMLDivElement>(null)
  const draggingKeyRef = useRef<string | null>(null)

  const SNAP_THRESHOLD = 2 // % — snap to centre when within this distance

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const obs = new ResizeObserver(() => setCanvasSize({ w: el.offsetWidth, h: el.offsetHeight }))
    obs.observe(el)
    setCanvasSize({ w: el.offsetWidth, h: el.offsetHeight })
    return () => obs.disconnect()
  }, [])

  const commit = useCallback((updated: CertificateFieldConfig[]) => {
    setElements(updated)
    onChange(JSON.stringify(updated))
  }, [onChange])

  const placedKeys = new Set(elements.map(uniqueKey))
  const availableDynamic = DYNAMIC_FIELDS.filter(f => !placedKeys.has(f))

  const addDynamic = (fieldId: FieldId) => {
    const dateLabels: Partial<Record<FieldId, string>> = {
      completedDate: 'Awarded on',
      expiryDate:    'Expires on',
    }
    const label = dateLabels[fieldId]
    const dateEl = defaultElement(fieldId)
    const toAdd: CertificateFieldConfig[] = label
      ? [
          {
            field: 'static', elementId: crypto.randomUUID(), staticText: label,
            x: dateEl.x, y: dateEl.y - 4,
            fontSize: 14, bold: false, align: dateEl.align, color: '#777777',
            fontFamily: dateEl.fontFamily,
          },
          dateEl,
        ]
      : [dateEl]
    commit([...elements, ...toAdd])
    setSelectedKey(fieldId)
  }

  const addStatic = () => {
    const el: CertificateFieldConfig = {
      field: 'static', elementId: crypto.randomUUID(), staticText: 'Awarded to',
      x: 50, y: 40, fontSize: 18, bold: false, align: 'center', color: '#555555',
    }
    commit([...elements, el])
    setSelectedKey(el.elementId!)
  }

  const addLogo = () => {
    const el: CertificateFieldConfig = {
      field: 'companyLogo', x: 50, y: 15,
      fontSize: 0, bold: false, align: 'center', color: '#000000', logoWidth: 20,
    }
    commit([...elements, el])
    setSelectedKey('companyLogo')
  }

  const addQr = () => {
    const el: CertificateFieldConfig = {
      field: 'verificationQr', x: 88, y: 85,
      fontSize: 0, bold: false, align: 'center', color: '#000000', logoWidth: 10,
    }
    commit([...elements, el])
    setSelectedKey('verificationQr')
  }

  const addTrainerSignature = () => {
    const el: CertificateFieldConfig = {
      field: 'trainerSignature', x: 30, y: 80,
      fontSize: 0, bold: false, align: 'center', color: '#1a1a1a', logoWidth: 22,
    }
    commit([...elements, el])
    setSelectedKey('trainerSignature')
  }

  const addCompanySignature = () => {
    const el: CertificateFieldConfig = {
      field: 'companySignature', x: 70, y: 80,
      fontSize: 0, bold: false, align: 'center', color: '#1a1a1a', logoWidth: 22,
    }
    commit([...elements, el])
    setSelectedKey('companySignature')
  }

  const addImage = (imageUrl: string, _imageName: string) => {
    const el: CertificateFieldConfig = {
      field: 'image', elementId: crypto.randomUUID(), imageUrl, imageWidth: 20,
      x: 50, y: 50, fontSize: 0, bold: false, align: 'center', color: '#000000',
    }
    commit([...elements, el])
    setSelectedKey(el.elementId!)
  }

  const removeElement = (key: string) => {
    commit(elements.filter(e => uniqueKey(e) !== key))
    if (selectedKey === key) setSelectedKey(null)
  }

  const updateElement = (key: string, patch: Partial<CertificateFieldConfig>) => {
    commit(elements.map(e => uniqueKey(e) === key ? { ...e, ...patch } : e))
  }

  const handleCanvasDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const fieldId = e.dataTransfer.getData('fieldId') as FieldId
    if (!fieldId) return
    const rect = canvasRef.current!.getBoundingClientRect()
    const x = Math.max(2, Math.min(98, ((e.clientX - rect.left) / rect.width) * 100))
    const y = Math.max(2, Math.min(98, ((e.clientY - rect.top) / rect.height) * 100))
    const dateLabels: Partial<Record<FieldId, string>> = {
      completedDate: 'Awarded on',
      expiryDate:    'Expires on',
    }
    const label = dateLabels[fieldId]
    const dateEl = { ...defaultElement(fieldId), x, y }
    const toAdd: CertificateFieldConfig[] = label
      ? [
          {
            field: 'static', elementId: crypto.randomUUID(), staticText: label,
            x, y: Math.max(2, y - 4),
            fontSize: 14, bold: false, align: dateEl.align, color: '#777777',
            fontFamily: dateEl.fontFamily,
          },
          dateEl,
        ]
      : [dateEl]
    commit([...elements, ...toAdd])
    setSelectedKey(fieldId)
  }

  const handleElementPointerDown = (e: React.PointerEvent, key: string) => {
    e.preventDefault(); e.stopPropagation()
    draggingKeyRef.current = key
    setIsDragging(true)
    setSelectedKey(key)
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  const handleCanvasPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const key = draggingKeyRef.current
    if (!key || !canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    let x = Math.max(1, Math.min(99, ((e.clientX - rect.left) / rect.width) * 100))
    let y = Math.max(1, Math.min(99, ((e.clientY - rect.top) / rect.height) * 100))

    const xLines: number[] = []
    const yLines: number[] = []
    let snappedX = false
    let snappedY = false

    // Snap to canvas centre
    if (Math.abs(x - 50) < SNAP_THRESHOLD) { x = 50; xLines.push(50); snappedX = true }
    if (Math.abs(y - 50) < SNAP_THRESHOLD) { y = 50; yLines.push(50); snappedY = true }

    // Snap to other elements' centres
    for (const el of elements) {
      if (uniqueKey(el) === key) continue
      if (!snappedX && Math.abs(x - el.x) < SNAP_THRESHOLD) {
        x = el.x; xLines.push(el.x); snappedX = true
      }
      if (!snappedY && Math.abs(y - el.y) < SNAP_THRESHOLD) {
        y = el.y; yLines.push(el.y); snappedY = true
      }
      if (snappedX && snappedY) break
    }

    setSnapGuides({ xLines, yLines })
    setElements(prev => prev.map(el => uniqueKey(el) === key ? { ...el, x, y } : el))
  }

  const handleCanvasPointerUp = () => {
    if (draggingKeyRef.current) {
      setElements(prev => { onChange(JSON.stringify(prev)); return prev })
      draggingKeyRef.current = null
      setIsDragging(false)
      setSnapGuides({ xLines: [], yLines: [] })
    }
  }

  const selectedEl = selectedKey ? elements.find(e => uniqueKey(e) === selectedKey) : null

  return (
    <div className="space-y-3">
      {/* Canvas — outer div has no overflow-hidden so the floating toolbar can escape */}
      <div ref={canvasRef}
        className="relative select-none"
        style={{ aspectRatio: isPortrait ? '1 / 1.414' : '1.414 / 1' }}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
        onDrop={handleCanvasDrop}
        onPointerMove={handleCanvasPointerMove}
        onPointerUp={handleCanvasPointerUp}
        onClick={() => setSelectedKey(null)}
      >
        {/* Background layer — clipped independently so elements/toolbar can overflow */}
        <div
          className="absolute inset-0 overflow-hidden rounded-lg border-2 border-dashed border-gray-200 pointer-events-none"
          style={{
            background: systemTemplate ? (backgroundColour ?? '#ffffff') : undefined,
            isolation: systemTemplate ? 'isolate' : undefined,
          }}
        >
          {backgroundImageUrl
            ? systemTemplate
              ? /* multiply maps white→bgColor; screen maps black→primaryColour; isolation confines blends to this div */
                <>
                  <img src={backgroundImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} style={{ mixBlendMode: 'multiply' }} />
                  {primaryColour && (
                    <div className="absolute inset-0" style={{ backgroundColor: primaryColour, mixBlendMode: 'screen' }} />
                  )}
                </>
              : <img src={backgroundImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            : <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-gray-300 text-sm">No background — upload one in the Backgrounds tab</div>}
        </div>

        {elements.map(el => {
          const key = uniqueKey(el)
          const isSelected = selectedKey === key
          const isLogo = el.field === 'companyLogo'
          const isImage = el.field === 'image'
          const isQr = el.field === 'verificationQr'
          const isTrainerSig = el.field === 'trainerSignature'
          const isCompanySig = el.field === 'companySignature'
          const isImageLike = isLogo || isImage || isQr || isTrainerSig || isCompanySig
          return (
            <div key={key}
              className={`absolute cursor-grab active:cursor-grabbing transition-[outline] rounded-sm ${isSelected ? 'outline outline-2 outline-indigo-500 outline-offset-2' : 'hover:outline hover:outline-1 hover:outline-indigo-300 hover:outline-offset-1'}`}
              style={{
                left: `${el.x}%`, top: `${el.y}%`, transform: 'translate(-50%, -50%)', userSelect: 'none',
                ...(isImageLike ? { width: `${isImage ? (el.imageWidth ?? 20) : (el.logoWidth ?? 15)}%` } : {
                  fontSize: `${el.fontSize * (canvasSize.h / 1000)}px`,
                  fontWeight: el.bold ? 700 : 400, color: el.color,
                  fontFamily: FONTS.find(f => f.id === el.fontFamily)?.css ?? 'Inter, sans-serif',
                  textAlign: el.align as React.CSSProperties['textAlign'],
                  whiteSpace: 'nowrap', lineHeight: 1.2,
                  textShadow: '0 1px 3px rgba(255,255,255,0.6)',
                }),
              }}
              onPointerDown={e => handleElementPointerDown(e, key)}
              onClick={e => { e.stopPropagation(); setSelectedKey(key) }}
            >
              {isLogo
                ? companyProfile?.logoUrl
                  ? <img src={companyProfile.logoUrl} alt="Company logo" className="w-full h-auto pointer-events-none" draggable={false} />
                  : <div className="flex items-center justify-center bg-white/60 border border-dashed border-gray-400 rounded px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">Company logo</div>
                : isQr
                  ? <div className="flex items-center justify-center bg-white/80 border border-dashed border-gray-400 rounded aspect-square p-[8%]">
                      <QrCode className="w-full h-full text-gray-700 opacity-70" strokeWidth={1.5} />
                    </div>
                : isTrainerSig || isCompanySig
                  ? <div className="flex flex-col items-center gap-0.5 bg-white/60 border border-dashed border-gray-400 rounded px-3 py-2 text-xs text-gray-500 whitespace-nowrap w-full">
                      <PenLine size={12} className="opacity-60" />
                      <span>{isTrainerSig ? 'Trainer signature' : "Director's signature"}</span>
                    </div>
                : isImage
                  ? el.imageUrl
                    ? <img src={el.imageUrl} alt="" className="w-full h-auto pointer-events-none" draggable={false} />
                    : <div className="flex items-center justify-center bg-white/60 border border-dashed border-gray-400 rounded px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">Image</div>
                  : previewText(el, companyName, region)}

              {isSelected && (
                <button type="button"
                  className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 cursor-pointer"
                  style={{ fontSize: '9px', lineHeight: 1 }}
                  onPointerDown={e => e.stopPropagation()}
                  onClick={e => { e.stopPropagation(); removeElement(key) }}>×</button>
              )}
            </div>
          )
        })}

        {elements.length === 0 && backgroundImageUrl && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-white/60 text-sm bg-black/20 px-3 py-1.5 rounded-full">Drag elements from below onto the certificate</p>
          </div>
        )}

        {/* Alignment guide lines — canvas centre snap and element-to-element snap */}
        {(() => {
          const xPositions: number[] = []
          const yPositions: number[] = []

          if (isDragging) {
            xPositions.push(...snapGuides.xLines)
            yPositions.push(...snapGuides.yLines)
          } else if (selectedEl) {
            if (selectedEl.x === 50) xPositions.push(50)
            if (selectedEl.y === 50) yPositions.push(50)
            for (const el of elements) {
              if (uniqueKey(el) === selectedKey) continue
              if (el.x === selectedEl.x && !xPositions.includes(el.x)) xPositions.push(el.x)
              if (el.y === selectedEl.y && !yPositions.includes(el.y)) yPositions.push(el.y)
            }
          }

          return (
            <>
              {xPositions.map(xp => (
                <div key={`gx-${xp}`} className="absolute inset-y-0 pointer-events-none"
                  style={{ left: `${xp}%`, width: '1px', background: 'rgba(99,102,241,0.7)', transform: 'translateX(-50%)' }} />
              ))}
              {yPositions.map(yp => (
                <div key={`gy-${yp}`} className="absolute inset-x-0 pointer-events-none"
                  style={{ top: `${yp}%`, height: '1px', background: 'rgba(99,102,241,0.7)', transform: 'translateY(-50%)' }} />
              ))}
            </>
          )
        })()}

        {/* Floating toolbar — appears below (or above if near bottom) the selected element */}
        {selectedEl && !isDragging && (() => {
          const showAbove = selectedEl.y > 72
          const isImageLike = isImageLikeField(selectedEl.field)
          return (
            <div
              className="absolute z-30 bg-white border border-gray-200 rounded-lg shadow-lg"
              style={{
                left: `${selectedEl.x}%`,
                top: showAbove ? `calc(${selectedEl.y}% - 14px)` : `calc(${selectedEl.y}% + 14px)`,
                transform: showAbove ? 'translateX(-50%) translateY(-100%)' : 'translateX(-50%)',
              }}
              onPointerDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
            >
              {/* Caret */}
              {showAbove
                ? <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45" />
                : <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-200 rotate-45" />}

              <div className="px-2.5 py-2 space-y-1.5">
                {selectedEl.field === 'static' && (
                  <input type="text" value={selectedEl.staticText ?? ''}
                    onChange={e => updateElement(selectedKey!, { staticText: e.target.value })}
                    className="block w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Custom text…"
                    style={{ minWidth: '160px' }}
                  />
                )}

                {(selectedEl.field === 'completedDate' || selectedEl.field === 'expiryDate') && (
                  <div className="flex gap-1 flex-wrap" style={{ maxWidth: '220px' }}>
                    {DATE_FORMATS.map(fmt => {
                      const active = (selectedEl.dateFormat ?? 'long') === fmt.id
                      return (
                        <button key={fmt.id} type="button"
                          onClick={() => updateElement(selectedKey!, { dateFormat: fmt.id })}
                          className={`px-1.5 py-0.5 rounded text-[10px] border cursor-pointer transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                          {fmt.label}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* Centre buttons — always shown */}
                <div className="flex items-center gap-1 pt-0.5 border-t border-gray-100">
                  <span className="text-[10px] text-gray-400 mr-0.5">Centre:</span>
                  <button type="button" title="Centre horizontally"
                    onClick={() => updateElement(selectedKey!, { x: 50 })}
                    className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer transition-colors ${selectedEl.x === 50 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                    ↔
                  </button>
                  <button type="button" title="Centre vertically"
                    onClick={() => updateElement(selectedKey!, { y: 50 })}
                    className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer transition-colors ${selectedEl.y === 50 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                    ↕
                  </button>
                  <button type="button" title="Centre both"
                    onClick={() => updateElement(selectedKey!, { x: 50, y: 50 })}
                    className={`px-2 py-0.5 rounded text-[10px] border cursor-pointer transition-colors ${selectedEl.x === 50 && selectedEl.y === 50 ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                    ⊕
                  </button>
                </div>

                {isImageLike ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500 whitespace-nowrap">Width %</span>
                    <input type="number" min="5" max="80"
                      value={(selectedEl.field === 'image' ? (selectedEl.imageWidth ?? 20) : (selectedEl.logoWidth ?? 15)) || ''}
                      onChange={e => {
                        const v = e.target.value === '' ? 0 : parseInt(e.target.value) || 0
                        updateElement(selectedKey!, selectedEl.field === 'image' ? { imageWidth: v } : { logoWidth: v })
                      }}
                      onBlur={e => {
                        const v = Math.min(80, Math.max(5, parseInt(e.target.value) || 15))
                        updateElement(selectedKey!, selectedEl.field === 'image' ? { imageWidth: v } : { logoWidth: v })
                      }}
                      className="w-16 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                ) : (
                  <>
                  <div className="flex items-center gap-1 flex-wrap" style={{ maxWidth: '260px' }}>
                    {FONTS.map(f => {
                      const active = (selectedEl.fontFamily ?? 'Inter') === f.id
                      return (
                        <button key={f.id} type="button"
                          onClick={() => updateElement(selectedKey!, { fontFamily: f.id })}
                          style={{ fontFamily: f.css }}
                          className={`px-2 py-1 rounded text-[11px] border cursor-pointer transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                          {f.label}
                        </button>
                      )
                    })}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Font size */}
                    <input type="number" min="6" max="120" value={selectedEl.fontSize || ''}
                      onChange={e => updateElement(selectedKey!, { fontSize: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 })}
                      onBlur={e => updateElement(selectedKey!, { fontSize: Math.min(120, Math.max(6, parseInt(e.target.value) || 20)) })}
                      className="w-14 text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      title="Font size"
                    />
                    {/* Bold */}
                    <button type="button"
                      onClick={() => updateElement(selectedKey!, { bold: !selectedEl.bold })}
                      className={`w-7 h-7 text-xs font-bold border rounded cursor-pointer transition-colors ${selectedEl.bold ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'}`}>
                      B
                    </button>
                    {/* Align */}
                    <div className="flex border border-gray-300 rounded overflow-hidden">
                      {(['left', 'center', 'right'] as const).map(a => {
                        const Icon = a === 'left' ? AlignLeft : a === 'center' ? AlignCenter : AlignRight
                        return (
                          <button key={a} type="button"
                            onClick={() => updateElement(selectedKey!, { align: a })}
                            className={`w-7 h-7 flex items-center justify-center cursor-pointer transition-colors ${selectedEl.align === a ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                            <Icon size={12} />
                          </button>
                        )
                      })}
                    </div>
                    {/* Colour */}
                    <input type="color" value={selectedEl.color}
                      onChange={e => updateElement(selectedKey!, { color: e.target.value })}
                      className="h-7 w-9 rounded border border-gray-300 cursor-pointer p-0.5"
                      title="Text colour"
                    />
                  </div>
                  </>
                )}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Palette */}
      <div className="flex flex-wrap items-start gap-2">
        {availableDynamic.map(fieldId => (
          <div key={fieldId} draggable
            onDragStart={e => { e.dataTransfer.setData('fieldId', fieldId); e.dataTransfer.effectAllowed = 'copy' }}
            onClick={() => addDynamic(fieldId)}
            title={`${fieldLabel(fieldId)} — drag to canvas or click to add`}
            className="group flex flex-col items-start gap-0.5 px-3 py-2 bg-white border border-gray-200 rounded-lg cursor-grab active:cursor-grabbing hover:border-indigo-400 hover:shadow-sm transition-all"
          >
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide group-hover:text-indigo-500">{fieldLabel(fieldId)}</span>
            <span className="text-gray-800 leading-tight" style={{ fontSize: fieldId === 'recipientName' ? '16px' : '13px', fontWeight: fieldId === 'recipientName' ? 700 : 400 }}>
              {previewText({ field: fieldId, x: 0, y: 0, fontSize: 24, bold: false, align: 'center', color: '#1a1a1a' }, companyName, region)}
            </span>
          </div>
        ))}

        <button type="button" onClick={addStatic}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-all">
          <Type size={14} /> Add custom text
        </button>

        {!placedKeys.has('companyLogo') && (
          <button type="button" onClick={addLogo}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-all">
            {companyProfile?.logoUrl
              ? <img src={companyProfile.logoUrl} alt="" className="h-5 w-auto object-contain" />
              : <span className="text-xs text-gray-400">🖼</span>}
            Company logo
          </button>
        )}

        {!placedKeys.has('verificationQr') && (
          isPro ? (
            <button type="button" onClick={addQr}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-all">
              <span className="text-xs">⬛</span>
              Verification QR
            </button>
          ) : (
            <button type="button" onClick={() => setShowProDialog(true)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400 hover:border-amber-300 hover:bg-amber-50 cursor-pointer transition-all">
              <span className="text-xs">⬛</span>
              Verification QR
              <span className="ml-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">Pro</span>
            </button>
          )
        )}

        {!placedKeys.has('trainerSignature') && (
          <button type="button" onClick={addTrainerSignature}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-all">
            <PenLine size={14} />
            Trainer signature
          </button>
        )}

        {!placedKeys.has('companySignature') && (
          <button type="button" onClick={addCompanySignature}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-all">
            <PenLine size={14} />
            Director's signature
          </button>
        )}
      </div>

      {/* Image library */}
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
            <Images size={11} /> Images
          </p>
          <button type="button"
            onClick={() => imageFileInputRef.current?.click()}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-indigo-600 hover:bg-indigo-50 rounded-md cursor-pointer transition-colors">
            <ImagePlus size={11} /> Upload
          </button>
          <input ref={imageFileInputRef} type="file" accept="image/*" className="hidden"
            onChange={e => {
              const f = e.target.files?.[0]
              if (!f) return
              setPendingImageFile(f)
              setPendingImageName(f.name.replace(/\.[^.]+$/, ''))
              e.target.value = ''
            }} />
        </div>

        {/* Pending upload — inline name entry */}
        {pendingImageFile && (
          <div className="mb-2 flex items-center gap-2 bg-white border border-indigo-200 rounded-lg p-2">
            <img src={URL.createObjectURL(pendingImageFile)} alt=""
              className="h-10 w-14 object-contain rounded border border-gray-100 bg-gray-50 shrink-0" />
            <input autoFocus
              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={pendingImageName}
              onChange={e => setPendingImageName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && pendingImageName.trim())
                  uploadImageMutation.mutate({ name: pendingImageName, file: pendingImageFile })
                if (e.key === 'Escape') { setPendingImageFile(null); setPendingImageName('') }
              }}
              placeholder="Image name…" />
            <button type="button"
              disabled={!pendingImageName.trim() || uploadImageMutation.isPending}
              onClick={() => uploadImageMutation.mutate({ name: pendingImageName, file: pendingImageFile })}
              className="shrink-0 px-2 py-1.5 text-[10px] font-semibold bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 cursor-pointer transition-colors">
              {uploadImageMutation.isPending ? '…' : 'Add'}
            </button>
            <button type="button"
              onClick={() => { setPendingImageFile(null); setPendingImageName('') }}
              className="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X size={13} />
            </button>
          </div>
        )}

        {certImages.length === 0 && !pendingImageFile ? (
          <p className="text-[10px] text-gray-400 text-center py-2">
            No images yet — upload one to place it on the certificate
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {certImages.map(img => (
              <button key={img.id} type="button"
                onClick={() => addImage(img.url, img.name)}
                title={`Place "${img.name}"`}
                className="flex flex-col items-center gap-1 p-1.5 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-sm cursor-pointer transition-all w-16">
                <img src={img.url} alt={img.name} className="h-10 w-full object-contain" />
                <span className="text-[9px] text-gray-500 truncate w-full text-center leading-tight">{img.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Style panel */}
      {selectedEl && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-700">{fieldLabel(selectedEl.field as FieldId | 'static' | 'companyLogo' | 'image' | 'verificationQr' | 'trainerSignature' | 'companySignature')}</p>
            <button type="button" className="text-xs text-gray-400 hover:text-red-500 cursor-pointer" onClick={() => removeElement(selectedKey!)}>Remove</button>
          </div>

          {selectedEl.field === 'static' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Text content</label>
              <input type="text" value={selectedEl.staticText ?? ''}
                onChange={e => updateElement(selectedKey!, { staticText: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g. Awarded to, Expires on…" />
            </div>
          )}

          {(selectedEl.field === 'completedDate' || selectedEl.field === 'expiryDate') && (
            <div className="space-y-2">
              <label className="block text-xs text-gray-500">Date format</label>
              <div className="flex flex-wrap gap-1.5">
                {DATE_FORMATS.map(fmt => {
                  const active = (selectedEl.dateFormat ?? 'long') === fmt.id
                  return (
                    <button key={fmt.id} type="button" onClick={() => updateElement(selectedKey!, { dateFormat: fmt.id })}
                      className={`px-2.5 py-1 rounded-md text-xs border cursor-pointer transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                      <span className="block font-medium">{fmt.label}</span>
                      <span className={`block font-normal ${active ? 'text-indigo-200' : 'text-gray-400'}`}>{formatDate(FORMAT_EXAMPLE_DATE, fmt.id, selectedEl.dateOrdinal ?? true, region)}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {isImageLikeField(selectedEl.field) ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Width (% of certificate)</label>
                <input type="number" min="5" max="80"
                  value={(selectedEl.field === 'image' ? (selectedEl.imageWidth ?? 20) : (selectedEl.logoWidth ?? 15)) || ''}
                  onChange={e => {
                    const v = e.target.value === '' ? 0 : parseInt(e.target.value) || 0
                    updateElement(selectedKey!, selectedEl.field === 'image' ? { imageWidth: v } : { logoWidth: v })
                  }}
                  onBlur={e => {
                    const v = Math.min(80, Math.max(5, parseInt(e.target.value) || 15))
                    updateElement(selectedKey!, selectedEl.field === 'image' ? { imageWidth: v } : { logoWidth: v })
                  }}
                  className="w-24 text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              {isSignatureField(selectedEl.field) && (
                <div className="space-y-2">
                  <label className="block text-xs text-gray-500">Printed name colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={selectedEl.color || '#1a1a1a'}
                      onChange={e => updateElement(selectedKey!, { color: e.target.value })}
                      className="h-[34px] w-12 rounded border border-gray-300 cursor-pointer p-0.5" />
                    <span className="text-xs text-gray-400 font-mono">{selectedEl.color}</span>
                  </div>
                  <p className="text-[10px] text-gray-400">
                    {selectedEl.field === 'trainerSignature'
                      ? 'Signature image or text is set in the trainer\'s profile. Falls back to company signature if not set.'
                      : 'Signature image or text is set in My Company → Signature settings.'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Font</label>
              <div className="flex flex-wrap gap-1.5">
                {FONTS.map(f => {
                  const active = (selectedEl.fontFamily ?? 'Inter') === f.id
                  return (
                    <button key={f.id} type="button" onClick={() => updateElement(selectedKey!, { fontFamily: f.id })}
                      style={{ fontFamily: f.css }}
                      className={`px-2 py-1 rounded-md text-xs border cursor-pointer transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                      {f.label}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Size</label>
                <input type="number" min="6" max="120" value={selectedEl.fontSize || ''}
                  onChange={e => updateElement(selectedKey!, { fontSize: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 })}
                  onBlur={e => updateElement(selectedKey!, { fontSize: Math.min(120, Math.max(6, parseInt(e.target.value) || 20)) })}
                  className="w-20 text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Weight</label>
                <button type="button" onClick={() => updateElement(selectedKey!, { bold: !selectedEl.bold })}
                  className={`px-3 py-1.5 text-sm font-bold border rounded-md cursor-pointer transition-colors ${selectedEl.bold ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'}`}>B</button>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Align</label>
                <div className="flex border border-gray-300 rounded-md overflow-hidden">
                  {(['left', 'center', 'right'] as const).map(a => {
                    const Icon = a === 'left' ? AlignLeft : a === 'center' ? AlignCenter : AlignRight
                    return (
                      <button key={a} type="button" onClick={() => updateElement(selectedKey!, { align: a })}
                        className={`px-2 py-1.5 cursor-pointer transition-colors ${selectedEl.align === a ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                        <Icon size={14} />
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Colour</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={selectedEl.color}
                    onChange={e => updateElement(selectedKey!, { color: e.target.value })}
                    className="h-[34px] w-12 rounded border border-gray-300 cursor-pointer p-0.5" />
                  <span className="text-xs text-gray-500 font-mono">{selectedEl.color}</span>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      )}

      {elements.length === 0 && (
        <p className="text-xs text-gray-400 text-center">Drag elements from the palette above onto the canvas, or click them to add at centre.</p>
      )}

      <ProUpgradeDialog
        open={showProDialog}
        onClose={() => setShowProDialog(false)}
        featureName="Verification QR codes"
      />
    </div>
  )
}
